import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { getCompetitionById } from "../../../services/competitionsServices";
import {
  changeCompetiteurPoids,
  deleteCompetiteur,
  deleteCompetiteurs,
  getCompetitionCompetiteurs,
  toggleCompetiteurPresence,
} from "../../../services/competiteursServices";
import { getCompetitionSettingsByCompetitionId } from "../../../services/competitionsettingsServices";

import { DEFAULT_FILTERS } from "../data/peseeData";
import useDebouncedValue from "./useDebouncedValue";

import {
  buildCsvRows,
  downloadCSV,
  enrichCompetiteurs,
  filterCompetiteurs,
  getOptionsFromRows,
  parseDisciplines,
  parseWeightInput,
  safeFileName,
} from "../utils/peseeUtils";

export default function usePeseePage({ competitionId, navigate }) {
  const [competition, setCompetition] = useState(null);
  const [settings, setSettings] = useState(null);
  const [judokas, setJudokas] = useState([]);

  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const [page, setPage] = useState(1);
  const [perPage, setPerPageState] = useState(50);

  const debouncedSearch = useDebouncedValue(filters.search, 300);

  const fetchCompetition = useCallback(async () => {
    try {
      const data = await getCompetitionById(competitionId);
      setCompetition(data);
    } catch {
      toast.error("Erreur lors du chargement de la compétition.");
      navigate?.("/");
    }
  }, [competitionId, navigate]);

  const fetchSettings = useCallback(async () => {
    try {
      const data = await getCompetitionSettingsByCompetitionId(competitionId);
      const nextSettings = Array.isArray(data) && data.length > 0 ? data[0] : data;

      setSettings(nextSettings || null);
    } catch {
      toast.error("Erreur lors du chargement des paramètres.");
    }
  }, [competitionId]);

  const fetchJudokas = useCallback(async () => {
    try {
      const data = await getCompetitionCompetiteurs(competitionId);
      setJudokas(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Erreur lors du chargement des compétiteurs.");
    }
  }, [competitionId]);

  const refresh = useCallback(async () => {
    setLoading(true);

    try {
      await Promise.all([fetchCompetition(), fetchSettings(), fetchJudokas()]);
    } finally {
      setLoading(false);
    }
  }, [fetchCompetition, fetchSettings, fetchJudokas]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const disciplinesList = useMemo(
    () => parseDisciplines(competition?.disciplines_competition),
    [competition]
  );

  const enrichedRows = useMemo(
    () =>
      enrichCompetiteurs({
        judokas,
        settings,
        disciplinesList,
      }),
    [judokas, settings, disciplinesList]
  );

  const options = useMemo(() => getOptionsFromRows(enrichedRows), [enrichedRows]);

  const filteredRows = useMemo(
    () =>
      filterCompetiteurs({
        rows: enrichedRows,
        filters,
        search: debouncedSearch,
      }),
    [enrichedRows, filters, debouncedSearch]
  );

  const total = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * perPage;
  const pageRows = filteredRows.slice(start, start + perPage);

  const quotaNumber =
    settings?.quota_competition != null
      ? Number(settings.quota_competition)
      : null;

  const totalInscrits = judokas.length;

  const quotaRatio =
    quotaNumber && quotaNumber > 0
      ? Math.min(1, totalInscrits / quotaNumber)
      : 0;

  const updateFilter = useCallback((key, value) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
    }));

    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  }, []);

  const setPerPage = useCallback((value) => {
    setPerPageState(value);
    setPage(1);
  }, []);

  const onTogglePresence = useCallback(async (rowId) => {
    setJudokas((current) =>
      current.map((competiteur) =>
        competiteur.id === rowId
          ? {
              ...competiteur,
              is_present: !competiteur.is_present,
            }
          : competiteur
      )
    );

    try {
      await toggleCompetiteurPresence(rowId);
    } catch {
      setJudokas((current) =>
        current.map((competiteur) =>
          competiteur.id === rowId
            ? {
                ...competiteur,
                is_present: !competiteur.is_present,
              }
            : competiteur
        )
      );

      toast.error("Erreur lors de la mise à jour de la présence.");
    }
  }, []);

  const onSaveWeight = useCallback(
    async (rowId, inputValue) => {
      const parsed = parseWeightInput(inputValue);

      if (!parsed?.valid) {
        toast.error(parsed?.message || "Poids invalide.");
        return false;
      }

      const previousRows = judokas;
      const rowBefore = previousRows.find((row) => row.id === rowId);
      const wasPresent = !!rowBefore?.is_present;

      setJudokas((current) =>
        current.map((competiteur) =>
          competiteur.id === rowId
            ? {
                ...competiteur,
                poids_competiteur: parsed.value,
                is_present: true,
              }
            : competiteur
        )
      );

      try {
        await changeCompetiteurPoids(rowId, parsed.value);

        if (!wasPresent) {
          try {
            await toggleCompetiteurPresence(rowId);
          } catch {
            toast.warn(
              "Poids enregistré, mais présence non synchronisée. Rafraîchissez la liste."
            );
          }
        }

        toast.success("Poids enregistré.");
        return true;
      } catch {
        setJudokas(previousRows);
        toast.error("Erreur lors de la mise à jour du poids.");
        return false;
      }
    },
    [judokas]
  );

  const onDelete = useCallback(async (rowId) => {
    if (!window.confirm("Supprimer ce compétiteur ?")) return;

    try {
      await deleteCompetiteur(rowId);

      setJudokas((current) =>
        current.filter((competiteur) => competiteur.id !== rowId)
      );

      toast.success("Compétiteur supprimé.");
    } catch {
      toast.error("Suppression impossible.");
    }
  }, []);

  const onDeleteFiltered = useCallback(async () => {
    if (!filteredRows.length) {
      toast.info("Aucun compétiteur filtré.");
      return;
    }

    if (
      !window.confirm(
        `Supprimer ${filteredRows.length} compétiteur(s) actuellement filtré(s) ?`
      )
    ) {
      return;
    }

    try {
      const ids = filteredRows.map((competiteur) => competiteur.id);
      const ok = await deleteCompetiteurs(ids);

      if (!ok) {
        toast.error("Suppression en lot impossible.");
        return;
      }

      toast.success("Compétiteurs supprimés.");
      resetFilters();
      fetchJudokas();
    } catch {
      toast.error("Erreur lors de la suppression en lot.");
    }
  }, [filteredRows, resetFilters, fetchJudokas]);

  const onExportCsv = useCallback(() => {
    if (!filteredRows.length) {
      toast.info("Aucun compétiteur à exporter selon vos filtres.");
      return;
    }

    const rows = buildCsvRows(filteredRows);
    const base = `pesees_${competition?.nom_competition || "competition"}_competiteurs`;

    downloadCSV(rows, `${safeFileName(base)}.csv`);
  }, [filteredRows, competition?.nom_competition]);

  return {
    competition,
    settings,
    judokas,
    loading,

    disciplinesList,
    filters,
    updateFilter,
    resetFilters,

    options,
    filteredRows,
    pageRows,
    total,
    page: currentPage,
    perPage,
    totalPages,
    setPage,
    setPerPage,

    quota: {
      quotaNumber,
      totalInscrits,
      ratio: quotaRatio,
    },

    actions: {
      refresh,
      fetchJudokas,
      onTogglePresence,
      onSaveWeight,
      onDelete,
      onDeleteFiltered,
      onExportCsv,
    },
  };
}